import {
    Box,
    Button,
    Typography,
    useMediaQuery,
    CircularProgress,
} from "@mui/material";
import { ArrowLeft, PlusCircle } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import LoadingGif from "../components/LoadingGif";
import { useState, useEffect } from "react";
import { useRoom } from "../context/RoomContext";
import { convertFromISO } from "../utils/dateValidation";
import { useHotel } from "../context/HotelContext";

const RoomManagement = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { id } = useParams();
    const isMobile = useMediaQuery("(max-width:950px)");
    const [filter, setFilter] = useState("all");
    const [isSaving, setIsSaving] = useState(false);
    const { createRoom, fetchHotelRooms, loading, updateRoomStatus, 
        duplicateRoom, deleteRoom, createPublishedRooms, deletePublishedRooms } = useRoom();
    const [rooms, setRooms] = useState([]);
    const { fetchDocuments, fetchDraft } = useHotel();
    const [roomPhotos, setRoomPhotos] = useState([]);
    const [showRepublishModal, setShowRepublishModal] = useState(false);
    const [showUnpublishModal, setShowUnpublishModal] = useState(false);
    const [showDuplicateModal, setShowDuplicateModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedRoomId, setSelectedRoomId] = useState(null);

    useEffect(() => {
        if (id) {
            fetchDraft(id);
        }
    }, [id, fetchDraft]);

    useEffect(() => {
        if (id) {
            fetchDocuments().then((response) => {
                const documents = response.data?.data || [];
                const roomPhotos = documents.filter((doc) => doc.type === "roomPhoto");
                setRoomPhotos(roomPhotos);
            }).catch((err) => console.error("Failed to fetch room photos:", err));
        }
    }, [id, fetchDocuments]);

    useEffect(() => {
        const fetchFilteredRooms = async () => {
            if (id) {
                const status = filter === "all" ? null : filter;
                const hotelRoom = await fetchHotelRooms(id, status);
                setRooms(hotelRoom);
            }
        };
        fetchFilteredRooms();
    }, [id, filter]);

    const handleEditRoom = async (roomId) => {
        try {
            navigate(`/hotel/${id}/room-info-1/${roomId}?manage=true`);
        } catch (err) {
            console.error("Error creating room:", err);
        }
    };

    const handleShowModal = (roomId, showModal) => {
        setSelectedRoomId(roomId);
        if (showModal === "unpublish") {
            setShowUnpublishModal(true);
        } else if (showModal === "duplicate") {
            setShowDuplicateModal(true);
        } else if (showModal === "delete") {
            setShowDeleteModal(true);
        } else {
            setShowRepublishModal(true);
        }
    };
    const handleUnpublishRoom = async (roomId) => {
        try {
            await updateRoomStatus(roomId, "unpublished");
            const response = await deletePublishedRooms(roomId); 
            console.log("Delete published room response:", response);
            setShowUnpublishModal(false);
            const status = filter === "all" ? null : filter;
            const hotelRoom = await fetchHotelRooms(id, status);
            setRooms(hotelRoom);
        } catch (err) {
            console.error("Error unpublishing room:", err);
        }
    };
    const handleRepublishRoom = async (roomId) => {
        try {
            await updateRoomStatus(roomId, "published");
            await createPublishedRooms(roomId);
            setShowRepublishModal(false);
            const status = filter === "all" ? null : filter;
            const hotelRoom = await fetchHotelRooms(id, status);
            setRooms(hotelRoom);
        } catch (err) {
            console.error("Error republishing room:", err);
        }
    };

    const handleDuplicateRoom = async (roomId) => {
        try {
            const newRoomId = await duplicateRoom(roomId);
            setShowDuplicateModal(false);
            navigate(`/hotel/${id}/room-info-1/${newRoomId}?manage=true`);
        } catch (err) {
            console.error("Error duplicating room:", err);
        }
    };

    const handleDeleteRoom = async (roomId) => {
        try {
            await deleteRoom(roomId);
            setShowDeleteModal(false);
            const status = filter === "all" ? null : filter;
            const hotelRoom = await fetchHotelRooms(id, status);
            setRooms(hotelRoom);
        } catch (err) {
            console.error("Error deleting room:", err);
        }
    };

    const handleCreateRoom = async () => {
        try {
            setIsSaving(true);
            const roomId = await createRoom(id);
            navigate(`/hotel/${id}/room-info-1/${roomId}?manage=true`);
        } catch (err) {
            console.error("Error creating room:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDisplayRoomPhotos = (roomId) => {
        const roomPhoto = roomPhotos.find((photo) => photo.roomId === roomId);
        return roomPhoto ? roomPhoto.url : "/icons/upload.svg";
    }

    const pageStyle = {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#F5F5F5",
    };

    const contentStyle = {
        flex: "1 0 auto",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        justifyContent: "flex-start",
        padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
        maxWidth: "1600px",
        width: "100%",
    };

    const filterButtonStyle = {
        borderRadius: "10px",
        padding: "8px 16px",
        textTransform: "none",
        fontSize: "16px",
        display: "flex",
        alignItems: "center",
        gap: 1,
        border: "1px solid #99A1AF",
        cursor: "pointer",
    };

    return (
        <div style={pageStyle}>
            <Navbar />
            <div style={contentStyle}>

                <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
                    {/* Heading */}
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 3 }}>
                        <Button
                            onClick={() => navigate("/hotel/onboarding")}
                            sx={{
                                minWidth: "auto",
                                width: isMobile ? "36px" : "44px",
                                height: isMobile ? "36px" : "44px",
                                borderRadius: "50%",
                                backgroundColor: "#31BACD",
                                color: "#fff",
                                padding: 0,
                                "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                            }}
                        >
                            <ArrowLeft size={isMobile ? 18 : 22} />
                        </Button>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, }}>
                            <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000" }}>
                                {t('RoomManagementTitle')}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: isMobile ? "13px" : "14px",
                                    color: "#666",
                                }}
                            >
                                {t('RoomManagementDesc')}
                            </Typography>
                        </Box>
                    </Box>
                    <Box sx={{ width: "100%" }}>
                        {/* Filter bar */}
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3, gap: 2, flexWrap: "wrap" }}>
                            <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
                                <Button
                                    onClick={() => setFilter("editing")}
                                    sx={{
                                        ...filterButtonStyle,
                                        backgroundColor: filter === "editing" ? "#FFB60D" : "#fff",
                                        color: filter === "editing" ? "#fff" : "#000",
                                        "&:hover": {
                                            backgroundColor: "#FFB60D",
                                        },
                                    }}
                                >
                                    <img src={filter === "editing" ? "/icons/roomEditingSelected.svg" : "/icons/roomEditing.svg"} alt="editing" style={{ width: "16px", height: "16px" }} />
                                    <div>{t('Editing')}</div>
                                </Button>
                                <Button
                                    onClick={() => setFilter("published")}
                                    sx={{
                                        ...filterButtonStyle,
                                        backgroundColor: filter === "published" ? "#00A63E" : "#fff",
                                        color: filter === "published" ? "#fff" : "#000",
                                        "&:hover": {
                                            backgroundColor: "#00A63E",
                                        },
                                    }}
                                >
                                    <img src={filter === "published" ? "/icons/roomPublishedSelected.svg" : "/icons/roomPublished.svg"} alt="published" style={{ width: "16px", height: "16px", }} />
                                    <div>{t('Published')}</div>
                                </Button>
                                <Button
                                    onClick={() => setFilter("unpublished")}
                                    sx={{
                                        ...filterButtonStyle,
                                        backgroundColor: filter === "unpublished" ? "#FF6900" : "#fff",
                                        color: filter === "unpublished" ? "#fff" : "#000",
                                        "&:hover": {
                                            backgroundColor: "#FF6900",
                                        },
                                    }}
                                >
                                    <img src={filter === "unpublished" ? "/icons/roomUnpublishedSelected.svg" : "/icons/roomUnpublished.svg"} alt="unpublished" style={{ width: "16px", height: "16px" }} />
                                    <div>{t('Unpublished')}</div>
                                </Button>
                                <Button
                                    onClick={() => setFilter("all")}
                                    sx={{
                                        color: filter === "all" ? "#31BACD" : "#999",
                                        textTransform: "none",
                                        fontSize: "14px",
                                        textDecoration: filter === "all" ? "underline" : "none",
                                    }}
                                >
                                    {t('ViewAll')}
                                </Button>
                            </Box>
                            <Button
                                onClick={handleCreateRoom}
                                sx={{
                                    backgroundColor: "#31BACD",
                                    color: "#fff",
                                    borderRadius: "10px",
                                    padding: "10px 16px",
                                    textTransform: "none",
                                    fontSize: "14px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                    "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                                }}
                            >

                                {isSaving ? <CircularProgress size={18} color="inherit" /> : <PlusCircle size={18} />} {t('AddRoom')}
                            </Button>
                        </Box>
                        {loading ? (
                            <LoadingGif />
                        ) : (
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2, backgroundColor: rooms.length === 0 && "#EFEFEF", padding: rooms.length === 0 && "32px 16px", borderRadius: "10px" }}>
                                {rooms.length === 0 && filter === "published" ? (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, }}>
                                        <img src="/icons/noRooms.svg" alt="paw" style={{ width: "16px", height: "auto" }} />
                                        <Box sx={{ fontSize: "16px", color: "#D9D9D9", fontWeight: 700, }}>
                                            {t('NoPublishedRoomYet')}
                                        </Box>
                                    </Box>
                                ) : rooms.length === 0 && filter === "unpublished" ? (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, }}>
                                        <img src="/icons/noRooms.svg" alt="paw" style={{ width: "16px", height: "auto" }} />
                                        <Box sx={{ fontSize: "16px", color: "#D9D9D9", fontWeight: 700, }}>
                                            {t('NoUnpublishedRoomYet')}
                                        </Box>
                                    </Box>
                                ) : rooms.length === 0 && filter === "editing" ? (
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, }}>
                                        <img src="/icons/noRooms.svg" alt="paw" style={{ width: "16px", height: "auto" }} />
                                        <Box sx={{ fontSize: "16px", color: "#D9D9D9", fontWeight: 700, }}>
                                            {t('NoEditingRoomYet')}
                                        </Box>
                                    </Box>
                                ) : (
                                    rooms.map((room) => (
                                        <Box
                                            key={room._id}
                                            sx={{
                                                display: "flex",
                                                gap: 2,
                                                padding: "16px",
                                                width: "100%",
                                                backgroundColor: "#fff",
                                                borderRadius: "10px",
                                                alignItems: "flex-start",
                                            }}
                                        >
                                            {/* Room thumbnail */}
                                            <Box
                                                sx={{
                                                    width: "80px",
                                                    height: "80px",
                                                    backgroundColor: "#E0E0E0",
                                                    borderRadius: "8px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    flexShrink: 0,
                                                    overflow: "hidden",
                                                }}
                                            >
                                                <Box component="img" src={handleDisplayRoomPhotos(room._id)} alt="room" sx={{ width: "100%", height: "auto", objectFit: "cover" }} />
                                            </Box>

                                            {/* Room info */}
                                            <Box sx={{ flex: 1 }}>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                                                    <Box
                                                        sx={{
                                                            backgroundColor: room.status === "editing" ? "#FFB60D" : room.status === "published" ? "#00A63E" : "#FF6900",
                                                            color: "#fff",
                                                            padding: "4px 12px",
                                                            borderRadius: "20px",
                                                            fontSize: "12px",
                                                            fontWeight: "600",
                                                        }}
                                                    >
                                                        {room.status === "editing" ? t('Editing') : room.status === "published" ? t('Published') : t('Unpublished')}
                                                    </Box>
                                                </Box>
                                                <Typography sx={{ fontWeight: "600", fontSize: "16px", color: "#000", mb: 1 }}>
                                                    {room.information1 ? room.information1.roomName : "-"}
                                                </Typography>
                                                <Box sx={{ display: "flex", gap: 1, fontSize: "13px", color: "#666", flexWrap: "wrap" }}>
                                                    <Typography sx={{ fontSize: "13px", color: "#666" }}>
                                                        {t('CreatedAt')} : {convertFromISO(room.createdAt)}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: "13px", color: "#666" }}>
                                                        |
                                                    </Typography>
                                                    {room.status === "editing" &&
                                                        <Typography sx={{ fontSize: "13px", color: "#666" }}>
                                                            {t('CurrentProgress')}：{t('RoomInfo')} {room.information3 ? "3" : room.information2 ? "2" : "1"}/3 |
                                                        </Typography>}
                                                    <Box
                                                        sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "13px", color: "#666", cursor: "pointer" }}
                                                        onClick={() => navigate(`/hotel/${id}/room-overview/${room._id}?view=true`)}
                                                    >
                                                        <img src="/icons/viewRoom.svg" alt="paw" style={{ width: "13", height: "auto" }} />
                                                        <Typography sx={{ fontSize: "13px", color: "#666" }}>
                                                            {t('View')} |
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "13px", color: "#666", cursor: "pointer" }}
                                                        onClick={() => handleShowModal(room._id, "duplicate")}
                                                    >
                                                        <img src="/icons/duplicateRoom.svg" alt="paw" style={{ width: "13", height: "auto" }} />
                                                        <Typography sx={{ fontSize: "13px", color: "#666" }}>
                                                            {t('Duplicate')}
                                                        </Typography>
                                                    </Box>
                                                    {room.status !== "published" &&
                                                        <Box
                                                            sx={{ display: "flex", alignItems: "center", gap: 0.5, fontSize: "13px", color: "#666", cursor: "pointer" }}
                                                            onClick={() => handleShowModal(room._id, "delete")}
                                                        >
                                                            <Typography sx={{ fontSize: "13px", color: "#666" }}>
                                                                |
                                                            </Typography>
                                                            <img src="/icons/deleteRoom.svg" alt="paw" style={{ width: "13", height: "auto" }} />
                                                            <Typography sx={{ fontSize: "13px", color: "#666" }}>
                                                                {" "}{t('Delete')}
                                                            </Typography>
                                                        </Box>
                                                    }
                                                </Box>
                                            </Box>

                                            {/* Action buttons */}
                                            <Box sx={{ display: "flex", gap: 1, flexDirection: "column", alignSelf: "center", }}>
                                                {room.status === "unpublished" &&
                                                    <Button
                                                        sx={{
                                                            backgroundColor: "#FFB60D",
                                                            color: "#fff",
                                                            padding: "6px 12px",
                                                            borderRadius: "6px",
                                                            textTransform: "none",
                                                            fontSize: "12px",
                                                            "&:hover": { backgroundColor: "#fbd782", },
                                                        }}
                                                        onClick={() => handleEditRoom(room._id)}
                                                    >
                                                        {t('Edit')}
                                                    </Button>
                                                }
                                                <Button
                                                    sx={{
                                                        backgroundColor: room.status === "editing" ? "#FFB60D" : room.status === "published" ? "#FF6900" : "#00A63E",
                                                        color: "#fff",
                                                        padding: "6px 12px",
                                                        borderRadius: "6px",
                                                        textTransform: "none",
                                                        fontSize: "12px",
                                                        "&:hover": { backgroundColor: room.status === "editing" ? "#fbd782" : room.status === "published" ? "#f7a368" : "#79cb97", },
                                                    }}
                                                    onClick={() => {
                                                        if (room.status === "editing") {
                                                            handleEditRoom(room._id);
                                                        } else if (room.status === "published") {
                                                            handleShowModal(room._id, "unpublish");
                                                        } else {
                                                            handleShowModal(room._id, "republish");
                                                        }
                                                    }}
                                                >
                                                    {room.status === "editing" ? t('Edit') : room.status === "published" ? t('Unpublish') : t('Republish')}
                                                </Button>
                                            </Box>
                                        </Box>
                                    ))
                                )}
                            </Box>
                        )}
                    </Box>
                </Box>
            </div>

            {(showRepublishModal || showUnpublishModal || showDuplicateModal || showDeleteModal) && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(0, 0, 0, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1300,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: isMobile ? 2 : 4,
                            backgroundColor: "#fff",
                            borderRadius: "16px",
                            padding: "24px",
                            width: isMobile ? "80%" : "auto",
                            maxWidth: "700px",
                            flexDirection: isMobile ? "column" : "row",
                        }}
                    >
                        <Box sx={{ width: isMobile ? "100%" : "320px", flexShrink: 0, }}>
                            <Box
                                component="img"
                                src="/icons/ppclogo.png"
                                alt="Pet Pet Club"
                                sx={{ height: isMobile ? "20px" : "24px", mb: 2 }}
                            />
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1, mb: 2 }}>
                                <Typography sx={{ fontSize: isMobile ? "16px" : "14px", color: "#000", fontWeight: "700" }}>
                                    {showUnpublishModal ? t('UnpublishRoomTitle') : showRepublishModal ? t('RepublishRoomTitle') : showDuplicateModal ? t('DuplicateRoomTitle') : t('DeleteRoomTitle')}
                                </Typography>
                                <Typography sx={{ fontSize: isMobile ? "16px" : "14px", color: "#666", }}>
                                    {showUnpublishModal ? t('UnpublishRoomDesc') : showRepublishModal ? t('RepublishRoomDesc') : showDuplicateModal ? t('DuplicateRoomDesc') : t('DeleteRoomDesc')}
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: "80%", }}>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => showUnpublishModal ? setShowUnpublishModal(false) : showRepublishModal ? setShowRepublishModal(false) : showDuplicateModal ? setShowDuplicateModal(false) : setShowDeleteModal(false)}
                                        sx={{
                                            backgroundColor: "#D9D9D9",
                                            color: "#fff",
                                            borderRadius: "10px",
                                            textTransform: "none",
                                            fontSize: isMobile ? "13px" : "14px",
                                            padding: "8px 16px",
                                            "&:hover": { backgroundColor: "#e6e6e6", },
                                        }}
                                    >
                                        {t('Back')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={() => showUnpublishModal ? handleUnpublishRoom(selectedRoomId) : showRepublishModal ? handleRepublishRoom(selectedRoomId) : showDuplicateModal ? handleDuplicateRoom(selectedRoomId) : handleDeleteRoom(selectedRoomId)}
                                        sx={{
                                            backgroundColor: showUnpublishModal ? "#FF6900" : showRepublishModal ? "#00A63E" : "#31BACD",
                                            color: "#fff",
                                            borderRadius: "10px",
                                            textTransform: "none",
                                            fontSize: isMobile ? "13px" : "14px",
                                            padding: "8px 16px",
                                            "&:hover": { backgroundColor: showUnpublishModal ? "#ff8c00" : showRepublishModal ? "#6ad391" : "#77c3cd", },
                                        }}
                                    >
                                        {loading ? <CircularProgress size={20} color="inherit" /> : showRepublishModal ? t('Republish') : showDuplicateModal ? t('Duplicate') : showDeleteModal ? t('Delete') : t('Unpublish')}
                                    </Button>
                                </Box>

                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
            <Footer />
        </div >
    );
};

export default RoomManagement;
